import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import { useGetProductBySlugQuery, useGetProductCategoriesQuery, useGetProductQuery } from '../../app/config/redux/slices/api/productApiSlice';
import { useGetReviewQuery, useCreateReviewMutation, useDeleteReviewMutation } from '../../app/config/redux/slices/api/reviewApiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart, removeFromCart, updateQuantity } from '../../app/config/redux/slices/cartSlice';
import BreadcrumbTwo from './BreadcrumbTwo';
import { skipToken } from '@reduxjs/toolkit/query';
import { Helmet } from "react-helmet-async";
import { share } from '../helper/share';

const ProductDetailsOne = () => {
    
  const productSlug = useParams().productSlug;
  const { data, error, isLoading } = useGetProductBySlugQuery({ slug : productSlug });
  const product = data?.product || [];
  const { data: categories = [] } = useGetProductCategoriesQuery();
   const [createReview ,{isLoading: createReviewIsLoading}]  = useCreateReviewMutation();
   const [deleteReview ,{isLoading: deleteReviewIsLoading}]  = useDeleteReviewMutation();
    const reviewQueryArgs = product?.id ? { product_id: product.id } : skipToken;
    console.log('Review Query Args:', reviewQueryArgs);
    const {
    data: reviewData,
    error: reviewError,
    isLoading: reviewIsLoading,
    refetch: refetchReviews
    } = useGetReviewQuery(reviewQueryArgs);
    // console.log('Review Data:', reviewData);
  const productImages = data?.product?.images || []
  const reviews = reviewData?.reviews?.data || [];

  
    const ratingCounts = [1, 2, 3, 4, 5].reduce((acc, star) => {
        acc[star] = reviews.filter(r => Number(r.rating) === star).length;
        return acc;
    }, {});

    const totalReviews = reviews.length;


    const averageRating = totalReviews > 0
    ? (
        reviews.reduce((sum, r) => sum + Number(r.rating), 0) / totalReviews
      ).toFixed(1)
    : 0;

    const productData = product?.product;

    /* -------- IMAGE HANDLING (Main + Gallery) -------- */

    const baseImageUrl = "https://app.villagezone.shop/images/";

    let imageUrls = [];

    /* Add main image */
    if (productData?.image) {
    const mainImage = productData.image.startsWith("http")
        ? productData.image
        : `${baseImageUrl}${productData.image}`;

    imageUrls.push(mainImage);
    }

    /* Add gallery images */
    if (productData?.images?.length > 0) {
    const galleryImages = productData.images.map((img) =>
        img.path.startsWith("http")
        ? img.path
        : `${baseImageUrl}${img.path}`
    );

    imageUrls = [...imageUrls, ...galleryImages];
    }

    /* Remove duplicates */
    imageUrls = [...new Set(imageUrls)];

    /* -------- PRICE CALCULATION -------- */

    const finalPrice =
    Number(productData?.discount) > 0
        ? Number(productData.price) -
        (Number(productData.price) * Number(productData.discount)) / 100
        : Number(productData?.price || 0);

    /* -------- BUILD PRODUCT SCHEMA -------- */

    const productSchema =
    productData?.id
        ? {
            "@context": "https://schema.org",
            "@type": "Product",

            name: productData.name,

            description:
            productData.short_description ||
            productData.description ||
            "",

            sku: String(productData.id),
            mpn: String(productData.id),

            category: "Grocery",

            url: window.location.href,

            image: imageUrls.map((url, index) => ({
            "@type": "ImageObject",
            contentUrl: url,
            caption: `${productData.name} - Image ${index + 1}`
            })),

            brand: {
            "@type": "Brand",
            name: productData.vendor_name,
            },

            seller: {
            "@type": "Organization",
            name: "Village Zone",
            url: "https://store.villagezone.shop"
            },

            offers: {
            "@type": "Offer",
            url: window.location.href,
            priceCurrency: "INR",
            price: finalPrice.toFixed(2),
            itemCondition: "https://schema.org/NewCondition",
            availability:
                Number(productData.quantity) > 0
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            },

            aggregateRating:
            reviews?.length > 0
                ? {
                    "@type": "AggregateRating",
                    ratingValue: averageRating,
                    reviewCount: reviews.length,
                }
                : undefined,

            review:
            reviews?.length > 0
                ? reviews.slice(0, 5).map((r) => ({
                    "@type": "Review",
                    author: {
                    "@type": "Person",
                    name: `Customer ${r.customer_id}`,
                    },
                    reviewRating: {
                    "@type": "Rating",
                    ratingValue: r.rating,
                    },
                    reviewBody: r.content,
                }))
                : undefined,
        }
        : null;

    /* -------- BREADCRUMB SCHEMA -------- */

    const breadcrumbSchema =
    productData?.id
        ? {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: window.location.origin
            },
            {
                "@type": "ListItem",
                position: 2,
                name: "Shop",
                item: `${window.location.origin}/shop`
            },
            {
                "@type": "ListItem",
                position: 3,
                name: productData.name,
                item: window.location.href
            }
            ]
        }
        : null;




    const renderStars = (rating) => {
        const fullStars = Math.floor(rating);
        const emptyStars = 5 - fullStars;

        return (
            <>
            {[...Array(fullStars)].map((_, i) => (
                <span
                key={`full-${i}`}
                className="text-15 fw-medium text-warning-600 d-flex"
                >
                <i className="ph-fill ph-star" />
                </span>
            ))}

            {[...Array(emptyStars)].map((_, i) => (
                <span
                key={`empty-${i}`}
                className="text-15 fw-medium text-gray-300 d-flex"
                >
                <i className="ph ph-star" />
                </span>
            ))}
            </>
        );
    };

    const getPercentage = (count) =>
    totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;



  

    const dispatch = useDispatch();
    const cartItem = useSelector((state) =>
        state.cart.cartItems.find((item) => item.product.id == product.id)
    );
    const [quantity, setQuantity] = useState(cartItem?.quantity ?? null);

    useEffect(() => {
        if (cartItem?.quantity !== undefined) {
            setQuantity(cartItem.quantity);
        } else {
            setQuantity(null);
        }
    }, [cartItem]);

    // Increment function
    const incrementQuantity = () => {
        const newQuantity = quantity + 1;
        setQuantity(newQuantity);

        dispatch(updateQuantity({
            product_id: product.id,
            quantity: newQuantity
        }));
        
    };

    const AddToCart = () => {
        const newQuantity = quantity + 1;
        setQuantity(newQuantity);

        dispatch(addToCart({ product }))
        
    };

    // Decrement function (optional)
    const decrementQuantity = () => {
        if (quantity > 1) {
        const newQty = quantity - 1;
        setQuantity(newQty);
        dispatch(updateQuantity({ product_id: product.id, quantity: newQty }));
        }else{
            setQuantity(null);
            dispatch(removeFromCart(product.id));
        }
    };
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');

    const [mainImage, setMainImage] = useState(null);

    const {userInfo} = useSelector((state) => state.auth);
    const isAuthenticated = !!userInfo?.access_token;
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await createReview({
                data: {
                    product_id: product.id,
                    title,
                    content,
                    rating,
                    // images: [] // placeholder
                },
                token: userInfo.access_token,
            }).unwrap();
            alert('Review submitted!');
            // Reset form
            setRating(0);
            setTitle('');
            setContent('');
            refetchReviews();
        } catch (error) {
            console.error('Error submitting review:', error);
            alert('Failed to submit review');
        }
    };
    const handleDeleteReview = async (reviewId) => {
        try {
            await deleteReview({
                review: reviewId,
                token: userInfo.access_token,
            }).unwrap();
            // Optionally refetch reviews or update local state
            alert('Review deleted');
            refetchReviews();
        } catch (err) {
            console.error('Failed to delete review:', err);
            alert('Failed to delete review');
        }
    };

    useEffect(() => {
        if (productImages.length > 0) {
            setMainImage(productImages[0].path); // assuming each image is an object with a `path`
        }else{
            console.log(data?.product?.image);
            setMainImage( 'https://villagezone.shop/images/' + data?.product?.image);
        }
    }, [productImages]);

    const settingsThumbs = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 1,
        focusOnSelect: true,
    };
    return (
        <>
            <Helmet>
                <title>
                    {`${product?.name ?? "Product"} ${product?.category ? `| ${product.category}` : ""} – VillageZone`}
                </title>

                <meta
                    name="description"
                    content={
                    product?.short_description
                        ? product.short_description.slice(0, 155)
                        : `Shop ${product?.name ?? "this product"} at VillageZone. Quality products, great prices, fast shipping.`
                    }
                />

                {productSchema && (
                    <script type="application/ld+json">
                    {JSON.stringify(productSchema)}
                    </script>
                )}

                {breadcrumbSchema && (
                    <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                    </script>
                )}

            </Helmet>


            {/* Breadcrumb */}
            <BreadcrumbTwo title={product.name} />
            <section className="product-details py-80">
                <div className="container container-lg">
                    <div className="row gy-4">
                        <div className="col-lg-9">
                            <div className="row gy-4">
                                <div className="col-xl-6">
                                    <div className="product-details__left">
                                        <div className="product-details__thumb-slider border border-gray-100 rounded-16">
                                            <div className="">
                                                <div className="product-details__thumb flex-center h-100">
                                                    <img
                                                        src={mainImage}
                                                        alt="Main Product"
                                                        style={{ width: 'auto', objectFit: 'contain' }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-24">
                                            <div className="product-details__images-slider">
                                                <Slider {...settingsThumbs}>
                                                    {productImages.map((image, index) => (
                                                        <div className="center max-w-120 max-h-120 h-100 flex-center border border-gray-100 rounded-16 p-8" key={index} onClick={() => setMainImage(image.path)}>
                                                            <img className='thum' src={image.path} alt={`Thumbnail ${index}`} />
                                                        </div>
                                                    ))}
                                                </Slider>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-6">
                                    <div className="product-details__content">
                                        <h1 style={{ fontSize: '24.68px' }} className="mb-12">{product.name}</h1>
                                        <div className="flex-align flex-wrap gap-12">
                                            <div className="flex-align gap-12 flex-wrap">
                                                    {reviewIsLoading ? (
                                                        <span className="text-sm text-gray-500">Loading reviews...</span>
                                                    ) : totalReviews === 0 ? (
                                                        <span className="text-sm text-gray-500">No reviews yet</span>
                                                    ) : (
                                                    <>
                                                        <div className="flex-align gap-8">
                                                        {renderStars(averageRating)}
                                                        </div>

                                                        <span className="text-sm fw-medium text-neutral-600">
                                                        {averageRating} Star Rating
                                                        </span>

                                                        <span className="text-sm fw-medium text-gray-500">
                                                        ({totalReviews})
                                                        </span>
                                                    </>
                                                    )}
                                            </div>
                                            <span className="text-sm fw-medium text-gray-500 ms-8">|</span>
                                            <span className="text-gray-900">
                                                {" "}
                                                <span className="text-gray-400 ">SKU:</span>{product.id}{" "}
                                            </span>
                                        </div>
                                        <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                                        <p className="text-gray-700">
                                            {product.short_description}
                                        </p>
                                        <div className="mt-32 flex-align flex-wrap gap-32">
                                            <div className="flex-align gap-8">
                                                <h4 className="mb-0">₹{product.price - (product.price / 100) * product.discount}</h4>
                                                {product.discount > 0 && (
                                                <span className="text-md text-gray-500">₹{product.price} </span>
                                                )}
                                            </div>
                                            {/* <Link to="#" className="btn btn-main rounded-pill">
                                                Order on What'sApp
                                            </Link> */}
                                        </div>
                                        <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                                        {/* <div className="flex-align flex-wrap gap-16 bg-color-one rounded-8 py-16 px-24">
                                            <div className="flex-align gap-16">
                                                <span className="text-main-600 text-sm">Special Offer:</span>
                                            </div>
                                            <div className="countdown" id="countdown11">
                                                <ul className="countdown-list flex-align flex-wrap">
                                                    <li className="countdown-list__item text-heading flex-align gap-4 text-xs fw-medium w-28 h-28 rounded-4 border border-main-600 p-0 flex-center">
                                                        3 <span className="days" />
                                                    </li>
                                                    <li className="countdown-list__item text-heading flex-align gap-4 text-xs fw-medium w-28 h-28 rounded-4 border border-main-600 p-0 flex-center">
                                                        5<span className="hours" />
                                                    </li>
                                                    <li className="countdown-list__item text-heading flex-align gap-4 text-xs fw-medium w-28 h-28 rounded-4 border border-main-600 p-0 flex-center">
                                                        36<span className="minutes" />
                                                    </li>
                                                    <li className="countdown-list__item text-heading flex-align gap-4 text-xs fw-medium w-28 h-28 rounded-4 border border-main-600 p-0 flex-center">
                                                        47<span className="seconds" />
                                                    </li>
                                                </ul>
                                            </div>
                                            <span className="text-gray-900 text-xs">
                                                Remains untill the end of the offer
                                            </span>
                                        </div> */}
                                        <div className="mb-24">
                                            <div className="mt-32 flex-align gap-12 mb-16">
                                                <span className="w-32 h-32 bg-white flex-center rounded-circle text-main-600 box-shadow-xl">
                                                    <i className="ph-fill ph-lightning" />
                                                </span>
                                                {product.quantity <= 20 && (
                                                    <h6 className="text-md mb-0 fw-bold text-gray-900">
                                                        Products are almost sold out
                                                    </h6>
                                                )}
                                            </div>
                                            <div
                                                className="progress w-100 bg-gray-100 rounded-pill h-8"
                                                role="progressbar"
                                                aria-label="Basic example"
                                                aria-valuenow={20}
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                            >
                                                <div
                                                    className="progress-bar bg-main-two-600 rounded-pill"
                                                    style={{ width: `${(product.quantity / 500) * 100}%` }}
                                                />
                                            </div>
                                            <span className="text-sm text-gray-700 mt-8">
                                                Stock left : {Number(product.quantity)}
                                            </span>
                                        </div>
                                        <span className="text-gray-900 d-block mb-8">Quantity : {product.net_quantity}</span>
                                        <div className="flex-between gap-16 flex-wrap">
                                            <div className="flex-align flex-wrap gap-16">
                                                {quantity != null && (
                                                    <div className="border border-gray-100 rounded-pill py-9 px-16 flex-align">
                                                        <button onClick={decrementQuantity}
                                                            type="button"
                                                            className="quantity__minus p-4 text-gray-700 hover-text-main-600 flex-center"
                                                        >
                                                            <i className="ph ph-minus" />
                                                        </button>
                                                        <input
                                                            type="number"
                                                            className="quantity__input border-0 text-center w-32"
                                                            value={quantity} readOnly
                                                        />
                                                        <button onClick={incrementQuantity}
                                                            type="button"
                                                            className="quantity__plus p-4 text-gray-700 hover-text-main-600 flex-center"
                                                        >
                                                            <i className="ph ph-plus" />
                                                        </button>
                                                    </div>
                                                )}
                                                {quantity == null && (
                                                    <button
                                                        onClick={AddToCart}
                                                        className="btn btn-main rounded-pill flex-align d-inline-flex gap-8 px-48"
                                                    >
                                                        <i className="ph ph-shopping-cart" /> Add To Cart
                                                    </button>
                                                )}

                                            </div>
                                            <div className="flex-align gap-12">
                                                {/* <Link
                                                    to="#"
                                                    className="w-52 h-52 bg-main-50 text-main-600 text-xl hover-bg-main-600 hover-text-white flex-center rounded-circle"
                                                >
                                                    <i className="ph ph-heart" />
                                                </Link>
                                                <Link
                                                    to="#"
                                                    className="w-52 h-52 bg-main-50 text-main-600 text-xl hover-bg-main-600 hover-text-white flex-center rounded-circle"
                                                >
                                                    <i className="ph ph-shuffle" />
                                                </Link> */}
                                                <button onClick={() => share()}
                                                    className="w-52 h-52 bg-main-50 text-main-600 text-xl hover-bg-main-600 hover-text-white flex-center rounded-circle"
                                                >
                                                    <i className="ph ph-share-network" />
                                                </button>
                                            </div>
                                        </div>
                                        <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                                        {/* <div className="flex-between gap-16 p-12 border border-main-two-600 border-dashed rounded-8 mb-16">
                                            <div className="flex-align gap-12">
                                                <button
                                                    type="button"
                                                    className="w-18 h-18 flex-center border border-gray-900 text-xs rounded-circle hover-bg-gray-100"
                                                >
                                                    <i className="ph ph-plus" />
                                                </button>
                                                <span className="text-gray-900 fw-medium text-xs">
                                                    Mfr. coupon. $3.00 off 5
                                                </span>
                                            </div>
                                            <Link
                                                to="/cart"
                                                className="text-xs fw-semibold text-main-two-600 text-decoration-underline hover-text-main-two-700"
                                            >
                                                View Details
                                            </Link>
                                        </div>
                                        <ul className="list-inside ms-12">
                                            <li className="text-gray-900 text-sm mb-8">
                                                Buy 1, Get 1 FREE
                                            </li>
                                            <li className="text-gray-900 text-sm mb-0">
                                                Buy 1, Get 1 FREE
                                            </li>
                                        </ul> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-3">
                            <div className="product-details__sidebar border border-gray-100 rounded-16 overflow-hidden">
                                <div className="p-24">
                                    <div className="flex-between bg-main-600 rounded-pill p-8">
                                        <div className="flex-align gap-8">
                                            <span className="w-44 h-44 bg-white rounded-circle flex-center text-2xl">
                                                <i className="ph ph-storefront" />
                                            </span>
                                            <span className="text-white">{product.vendor_name}</span>
                                        </div>
                                        <Link
                                            to={`/shop?vendor=${product.vendor_id}`}
                                            className="btn btn-white rounded-pill text-uppercase"
                                        >
                                            View Store
                                        </Link>
                                    </div>
                                </div>
                                <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                    <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                        <i className="ph-fill ph-truck" />
                                    </span>
                                    <div className="">
                                        <h6 className="text-sm mb-8">Fast Delivery</h6>
                                        <p className="text-gray-700">
                                            Lightning-fast shipping, guaranteed.
                                        </p>
                                    </div>
                                </div>
                                {/* <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                    <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                        <i className="ph-fill ph-arrow-u-up-left" />
                                    </span>
                                    <div className="">
                                        <h6 className="text-sm mb-8">Free 90-day returns</h6>
                                        <p className="text-gray-700">Shop risk-free with easy returns.</p>
                                    </div>
                                </div> */}
                                {/* <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                    <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                        <i className="ph-fill ph-check-circle" />
                                    </span>
                                    <div className="">
                                        <h6 className="text-sm mb-8">
                                            Pickup available at Shop location
                                        </h6>
                                        <p className="text-gray-700">Usually ready in 24 hours</p>
                                    </div>
                                </div> */}
                                <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                    <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                        <i className="ph-fill ph-credit-card" />
                                    </span>
                                    <div className="">
                                        <h6 className="text-sm mb-8">Payment</h6>
                                        <p className="text-gray-700">
                                            Payment upon receipt of goods, Payment by card in the
                                            department, Google Pay, Online card.
                                        </p>
                                    </div>
                                </div>
                                {/* <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                    <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                        <i className="ph-fill ph-check-circle" />
                                    </span>
                                    <div className="">
                                        <h6 className="text-sm mb-8">Warranty</h6>
                                        <p className="text-gray-700">
                                            The Consumer Protection Act does not provide for the return of
                                            this product of proper quality.
                                        </p>
                                    </div>
                                </div> */}
                                <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                    <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                        <i className="ph-fill ph-package" />
                                    </span>
                                    <div className="">
                                        <h6 className="text-sm mb-8">Packaging</h6>
                                        <p className="text-gray-700">
                                            Each product is carefully packed by the vendor themselves, 
                                            ensuring authenticity, quality &amp; care from their hands 
                                            to your home
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="pt-80">
                        <div className="product-dContent border rounded-24">
                            <div className="product-dContent__header border-bottom border-gray-100 flex-between flex-wrap gap-16">
                                <ul
                                    className="nav common-tab nav-pills mb-3"
                                    id="pills-tab"
                                    role="tablist"
                                >
                                    <li className="nav-item" role="presentation">
                                        <button
                                            className="nav-link active"
                                            id="pills-description-tab"
                                            data-bs-toggle="pill"
                                            data-bs-target="#pills-description"
                                            type="button"
                                            role="tab"
                                            aria-controls="pills-description"
                                            aria-selected="true"
                                        >
                                            Description
                                        </button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button
                                            className="nav-link"
                                            id="pills-reviews-tab"
                                            data-bs-toggle="pill"
                                            data-bs-target="#pills-reviews"
                                            type="button"
                                            role="tab"
                                            aria-controls="pills-reviews"
                                            aria-selected="false"
                                        >
                                            Reviews
                                        </button>
                                    </li>
                                </ul>
                                <Link
                                    to="#"
                                    className="btn bg-color-one rounded-16 flex-align gap-8 text-main-600 hover-bg-main-600 hover-text-white"
                                >
                                    <img src="/assets/images/icon/satisfaction-icon.png" alt="" />
                                    100% Satisfaction Guaranteed
                                </Link>
                            </div>
                            <div className="product-dContent__box">
                                <div className="tab-content" id="pills-tabContent">
                                    <div
                                        className="tab-pane fade show active"
                                        id="pills-description"
                                        role="tabpanel"
                                        aria-labelledby="pills-description-tab"
                                        tabIndex={0}
                                    >
                                        <div className="mb-40">
                                            <h6 className="mb-24">Product Description</h6>
                                            <p>
                                                {product.description}
                                            </p>
                                        </div>
                                        <div className="mb-40">
                                            <h6 className="mb-24">Product Specifications</h6>
                                            <ul className="mt-32">
                                                {product?.sub_category_id && (
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        Product Type:
                                                        <span className="text-gray-500 ms-8">{categories?.find(cat => cat.id === product.sub_category_id)?.name}</span>
                                                    </span>
                                                </li>
                                                )}
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        Product Name:
                                                        <span className="text-gray-500 ms-8">
                                                            {" "}
                                                            {product.name}{" "}
                                                        </span>
                                                    </span>
                                                </li>
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        Brand:
                                                        <span className="text-gray-500 ms-8"> {product.vendor_name}</span>
                                                    </span>
                                                </li>
                                                {product?.fssai_license_no && (
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        FSA Certified:
                                                        <span className="text-gray-500 ms-8"> {product.fssai_license_no}</span>
                                                    </span>
                                                </li>
                                                )}
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        Food Type
                                                        <span className="text-gray-500 ms-8"> {product.food_type}</span>
                                                    </span>
                                                </li>
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        Size/Count:
                                                        <span className="text-gray-500 ms-8"> {product.net_quantity}</span>
                                                    </span>
                                                </li>
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        Item Code:
                                                        <span className="text-gray-500 ms-8"> {product.id}</span>
                                                    </span>
                                                </li>
                                                {product?.ingredients && (
                                                    <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-heading fw-medium">
                                                        Ingredients:
                                                        <span className="text-gray-500 ms-8">
                                                           {product.ingredients}
                                                        </span>
                                                    </span>
                                                </li>

                                                )}

                                            </ul>
                                        </div>
                                        {product?.nutrition_facts?.length > 0 && (
                                        <div className="mb-40">
                                            <h6 className="mb-24">Nutrition Facts</h6>
                                            <ul className="mt-32">
                                                {product.nutrition_facts.map((item, index) => (
                                                <li  key={index} className="text-gray-400 mb-14 flex-align gap-14">
                                                    {/* <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span> */}
                                                    <span className="text-heading fw-medium">
                                                        {item.key.charAt(0).toUpperCase() + item.key.slice(1)}:
                                                        <span className="text-gray-500 ms-8">
                                                             {item.value}
                                                        </span>
                                                    </span>
                                                </li>
                                                ))}
                                            </ul>
                                        </div>
                                        )}
                                        {/* <div className="mb-0">
                                            <h6 className="mb-24">More Details</h6>
                                            <ul className="mt-32">
                                                <li className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-gray-500">
                                                        {" "}
                                                        ...
                                                    </span>
                                                </li>
                                            </ul>
                                        </div> */}
                                    </div>
                                    <div
                                        className="tab-pane fade"
                                        id="pills-reviews"
                                        role="tabpanel"
                                        aria-labelledby="pills-reviews-tab"
                                        tabIndex={0}
                                    >
                                        <div className="row g-4">
                                            <div className="col-lg-6">
                                                <h6 className="mb-24">Product Reviews</h6>
                                                {reviews.map((review) => (
                                                    <div className="d-flex align-items-start gap-24 pb-44 border-bottom border-gray-100 mb-44">
                                                        {/* <img
                                                            src="/assets/images/thumbs/comment-img1.png"
                                                            alt=""
                                                            className="w-52 h-52 object-fit-cover rounded-circle flex-shrink-0"
                                                        /> */}
                                                        <div className="flex-grow-1">
                                                            <div className="flex-between align-items-start gap-8 ">
                                                                <div className="">
                                                                    <h6 className="mb-12 text-md">Customer #{review.customer_id}</h6>
                                                                    <div className="flex-align gap-8">
                                                                        <span className="text-15 fw-medium text-warning-600 d-flex">
                                                                            <i className={`ph-fill ${review.rating >= 1 ? 'ph-star' : 'ph-star-light'}`} />
                                                                        </span>
                                                                        <span className="text-15 fw-medium text-warning-600 d-flex">
                                                                            <i className={`ph-fill ${review.rating >= 2 ? 'ph-star' : 'ph-star-light'}`} />
                                                                        </span>
                                                                        <span className="text-15 fw-medium text-warning-600 d-flex">
                                                                            <i className={`ph-fill ${review.rating >= 3 ? 'ph-star' : 'ph-star-light'}`} />
                                                                        </span>
                                                                        <span className="text-15 fw-medium text-warning-600 d-flex">
                                                                            <i className={`ph-fill ${review.rating >= 4 ? 'ph-star' : 'ph-star-light'}`} />
                                                                        </span>
                                                                        <span className="text-15 fw-medium text-warning-600 d-flex">
                                                                            <i className={`ph-fill ${review.rating >= 5 ? 'ph-star' : 'ph-star-light'}`} />
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                {/* <span className="text-gray-800 text-xs">
                                                                    3 Days ago
                                                                </span> */}
                                                            </div>
                                                            <h6 className="mb-14 text-md mt-24">{review.title}</h6>
                                                            <p className="text-gray-700">
                                                                {review.content}
                                                            </p>
                                                            <div className="flex-align gap-20 mt-44">
                                                                {isAuthenticated && review.customer_id === userInfo.id && (
                                                                <button
                                                                    onClick={() => handleDeleteReview(review.id)}
                                                                    className="flex-align gap-12 text-gray-700 hover-text-main-600"
                                                                >
                                                                    <i className="ph-bold ph-trash" />
                                                                    Delete
                                                                </button>
                                                                )}
                                                                {/* <Link
                                                                    to="#comment-form"
                                                                    className="flex-align gap-12 text-gray-700 hover-text-main-600"
                                                                >
                                                                    <i className="ph-bold ph-arrow-bend-up-left" />
                                                                    Replay
                                                                </Link> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                <div className="mt-56">
                                                    <div className="">
                                                        <h6 className="mb-24">Write a Review</h6>
                                                        <span className="text-heading mb-8">
                                                            What is it like to Product?
                                                        </span>
                                                        <div className="flex-align gap-8 mb-16">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <span
                                                                    key={star}
                                                                    onClick={() => setRating(star)}
                                                                    onMouseEnter={() => setHoverRating(star)}
                                                                    onMouseLeave={() => setHoverRating(0)}
                                                                    className={`cursor-pointer text-15 fw-medium d-flex ${
                                                                        (hoverRating || rating) >= star ? 'text-warning-600' : 'text-neutral-400'
                                                                    }`}
                                                                >
                                                                    <i className="ph-fill ph-star"></i>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="mt-32">
                                                        <form onSubmit={handleSubmit}>
                                                            <div className="mb-32">
                                                                <label
                                                                    htmlFor="title"
                                                                    className="text-neutral-600 mb-8"
                                                                >
                                                                    Review Title
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="common-input rounded-8"
                                                                    id="title"
                                                                    placeholder="Great Product"
                                                                    value={title}
                                                                    onChange={(e) => setTitle(e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="mb-32">
                                                                <label
                                                                    htmlFor="desc"
                                                                    className="text-neutral-600 mb-8"
                                                                >
                                                                    Review Content
                                                                </label>
                                                                <textarea
                                                                    className="common-input rounded-8"
                                                                    id="desc"
                                                                    placeholder="Describe your experience"
                                                                    value={content}
                                                                    onChange={(e) => setContent(e.target.value)}
                                                                />
                                                            </div>
                                                            <button
                                                                type="submit"
                                                                className="btn btn-main rounded-pill mt-48"
                                                            >
                                                                Submit Review
                                                            </button>
                                                        </form>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="ms-xxl-5">
                                                    <h6 className="mb-24">Customers Feedback</h6>
                                                    <div className="d-flex flex-wrap gap-44">
                                                        <div className="border border-gray-100 rounded-8 px-40 py-52 flex-center flex-column flex-shrink-0 text-center">
                                                            <h2 className="mb-6 text-main-600">{averageRating}</h2>
                                                            <div className="flex-center gap-8">
                                                                {renderStars(averageRating)}
                                                            </div>
                                                            <span className="mt-16 text-gray-500">
                                                                Based on {totalReviews} reviews
                                                            </span>
                                                        </div>
                                                        <div className="border border-gray-100 rounded-8 px-24 py-40 flex-grow-1">
                                                            {[5, 4, 3, 2, 1].map((star) => {
                                                                const count = ratingCounts[star] || 0;
                                                                const percent = getPercentage(count);

                                                                return (
                                                                    <div key={star} className="flex-align gap-8 mb-20">
                                                                        <span className="text-gray-900 flex-shrink-0">{star}</span>

                                                                        <div
                                                                            className="progress w-100 bg-gray-100 rounded-pill h-8"
                                                                            role="progressbar"
                                                                            aria-valuenow={percent}
                                                                            aria-valuemin={0}
                                                                            aria-valuemax={100}
                                                                        >
                                                                            <div
                                                                                className="progress-bar bg-main-600 rounded-pill"
                                                                                style={{ width: `${percent}%` }}
                                                                            />
                                                                        </div>

                                                                        <div className="flex-align gap-2">
                                                                            {[...Array(star)].map((_, i) => (
                                                                                <span key={i} className="text-xs text-warning-600 d-flex">
                                                                                    <i className="ph-fill ph-star" />
                                                                                </span>
                                                                            ))}
                                                                        </div>

                                                                        <span className="text-gray-900 flex-shrink-0">{count}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>


    )
}

export default ProductDetailsOne